/**
 * @fileoverview Typography component for rendering text with markdown support
 * @example
 * // Usage in a component:
 * import { Text, TextLink, BoldCaption } from './Typography'
 *
 * function Component() {
 *   return (
 *     <div>
 *       <Text>Regular text</Text>
 *       <TextLink href="https://example.com">Link text</TextLink>
 *       <BoldCaption>Bold text</BoldCaption>
 *     </div>
 *   )
 * }
 */

import type { ComponentPropsWithoutRef, PropsWithChildren } from 'react'
import ReactMarkdown from 'react-markdown'
import css from './Typography.module.css'

const components = {
    a: (props: PropsWithChildren) => (
        <a {...props} target="_blank" rel="noreferrer">
            {props.children}
        </a>
    ),
    ul: (props: PropsWithChildren) => <ul className={css.list}>{props.children}</ul>,
}

/**
 * @function Text
 * @description Component for rendering text with markdown support
 * @param {PropsWithChildren & React.HTMLAttributes<HTMLSpanElement>} props - The props for the Text component
 * @returns {JSX.Element} The Text component
 */
export function Text(props: PropsWithChildren & React.HTMLAttributes<HTMLSpanElement>) {
    return (
        <ReactMarkdown
            components={components}
            className={[css.text, props.className].join(' ')}
        >{`${props.children}`}</ReactMarkdown>
    )
}

/**
 * @function Comment
 * @description Component for rendering a comment with markdown support
 * @param {string} props.comment - The comment to render
 * @returns {JSX.Element} The Comment component
 */
export function Comment(props: { comment: string }) {
    return (
        <ReactMarkdown components={components} className={css.comment}>
            {props.comment}
        </ReactMarkdown>
    )
}

/**
 * @interface CaptionLinkProps
 * @description Props for the CaptionLink component
 * @extends {ComponentPropsWithoutRef<'div'>}
 * @property {string} href - The href attribute for the link
 */
interface CaptionLinkProps extends ComponentPropsWithoutRef<'div'> {
    href: string
}

/**
 * @function TextLink
 * @description Component for rendering a link with markdown support
 * @param {CaptionLinkProps} props - The props for the TextLink component
 * @returns {JSX.Element} The TextLink component
 */
export function TextLink(props: CaptionLinkProps) {
    return (
        <a
            href={props.href}
            rel="noreferrer"
            className={`${css.link} ${props.className}`}
            download
            target="_blank"
        >
            <Text>{props.children}</Text>
        </a>
    )
}

/**
 * @function BoldCaption
 * @description Component for rendering a bold caption
 * @param {ComponentPropsWithoutRef<'span'>} props - The props for the BoldCaption component
 * @returns {JSX.Element} The BoldCaption component
 */
export function BoldCaption(props: ComponentPropsWithoutRef<'span'>) {
    return <span className={`${css.caption} ${css.bold} ${props.className}`}>{props.children}</span>
}
